import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  AlertContextType,
  AlertProviderProps,
  AlertType,
  AlertTypes,
} from "./types";
import { generateId } from "../../utils/generateId";
import { SuccessAlert } from "./components/SuccessAlert";
import { ErrorAlert } from "./components/ErrorAlert";

import styles from "./AlertsContext.module.scss";

const ALERT_TIMEOUT = 5000;
const AlertTypesMap = {
  [AlertTypes.SUCCESS]: SuccessAlert,
  [AlertTypes.ERROR]: ErrorAlert,
};
const AlertsContext = createContext<AlertContextType>({} as AlertContextType);

export const useAlerts = () => {
  const context = AlertsContext;
  if (!context) {
    throw new Error("useAlertContext must be used within an AlertProvider");
  }
  return useContext(context);
};

export const AlertsProvider = ({ children }: AlertProviderProps) => {
  const [alerts, setAlerts] = useState<Map<string, AlertType>>(new Map());

  const removeAlert = useCallback((id: string) => {
    setAlerts((prevAlerts) => {
      const newAlerts = new Map(prevAlerts);
      const alert = newAlerts.get(id);

      if (alert) {
        clearTimeout(alert.timer);
        newAlerts.delete(id);
      }

      return newAlerts;
    });
  }, []);

  const pushAlert = useCallback(
    (type: AlertTypes, message: string) => {
      const id = generateId();

      const timer = setTimeout(
        (alertId) => {
          removeAlert(alertId);
        },
        ALERT_TIMEOUT,
        id,
      );

      setAlerts((prevAlerts) => {
        const newAlerts = new Map(prevAlerts);
        newAlerts.set(id, { id, type, message, timer });
        return newAlerts;
      });
    },
    [removeAlert],
  );

  useEffect(() => {
    return () => {
      alerts.forEach((alert) => {
        clearTimeout(alert.timer);
      });
      setAlerts(new Map());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const alertsToRender = Array.from(alerts.values());

  const alertsElements = alertsToRender.map((alert) => {
    const AlertComponent = AlertTypesMap[alert.type];
    return (
      <li key={alert.id} className={styles["alertItem"]}>
        <AlertComponent
          message={alert.message}
          onClose={() => removeAlert(alert.id)}
        />
      </li>
    );
  });

  const contextValue = useMemo(
    () => ({
      alerts,
      pushAlert,
      removeAlert,
    }),
    [alerts, pushAlert, removeAlert],
  );

  return (
    <AlertsContext.Provider value={contextValue}>
      <>
        {children}
        {alertsToRender.length
          ? createPortal(
              <ul className={styles["alertList"]}>{alertsElements}</ul>,
              document.body,
            )
          : null}
      </>
    </AlertsContext.Provider>
  );
};
